/** @jsx jsx */
import React, {
	useLayoutEffect,
	type ReactElement,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type PropsWithChildren,
} from 'react';
import { stringify as stringifyOld } from 'querystring';
import { styled } from '@compiled/react';
import { stringify } from 'query-string';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { type CustomTriggerProps, DropdownItem } from '@atlaskit/dropdown-menu';
import ExportIcon from '@atlaskit/icon/core/upload';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { cssMap, jsx } from '@atlaskit/css';
import { expVal } from '@atlassian/jira-feature-experiments';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import AsyncExportIssues from '@atlassian/jira-issue-navigator-action-export-issues/src/ui/async.tsx';
import MeatballMenu from '@atlassian/jira-issue-navigator-action-meatball-menu/src/ui/index.tsx';
import {
	GO_TO_ALL_ISSUES,
	HIERARCHY_TOGGLE,
	HIDE_DONE_TOGGLE,
} from '@atlassian/jira-issue-navigator-action-meatball-menu/src/ui/constants.tsx';
import QuickFiltersDropdown from '@atlassian/jira-issue-navigator-action-quick-filters/src/index.tsx';
import { FilterIconButton } from '@atlassian/jira-issue-navigator-actions-common/src/ui/filter-icon-button/index.tsx';
import { isFilterId } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import type { SearchResultPageData } from '@atlassian/jira-issue-navigator/src/common/types.tsx';
import {
	markOnce,
	marks,
} from '@atlassian/jira-issue-navigator/src/common/utils/performance-analytics.tsx';
import ViewSwitcher from '@atlassian/jira-issue-navigator/src/ui/view-switcher/index.tsx';
import { useNaturalLanguageMode } from '@atlassian/jira-jql-builder-with-ai/src/controllers/natural-language-mode/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { IssueNavigatorFeatureGuard } from '@atlassian/jira-issue-navigator/src/controllers/features-context/index.tsx';
import messages from './messages.tsx';

type Props = {
	jql: string;
	filterId?: string;
	isModifiedFilter: boolean;
	isIssueHierarchySupportEnabled?: boolean;
	isHideDoneSupportEnabled?: boolean;
	isHideDoneWorkItemsEnabled?: boolean;
	isGridNavigationEnabled?: boolean;
	isConditionalFormattingEnabled?: boolean;
	searchResultPageData: SearchResultPageData;
	isQuickFiltersDisabled?: boolean;
	HierarchyToggle: React.ReactNode;
	GroupByDropdown: React.ReactNode;
	FiltersMenu?: React.ReactNode;
	HideDoneToggle: React.ReactNode;
	SaveResetViewSettingsButton: React.ReactNode;
	onChangeFilter?: (filterId?: string) => void;
	isInlineFieldConfigEnabled?: boolean;
	onChangeJqlAndGroupBy?: (params: { jql?: string; groupBy?: string }) => void;
};

const HierarchyToggleWrapper = ({ children }: { children: React.ReactNode }) => (
	// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
	<Box
		paddingBlock="space.050"
		paddingInline="space.150"
		onClick={(e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation()}
	>
		{children}
	</Box>
);

const HideDoneToggleWrapper = ({ children }: { children: React.ReactNode }) => (
	// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable
	<Box
		paddingBlock="space.050"
		paddingInline="space.150"
		onClick={(e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation()}
	>
		{children}
	</Box>
);

const MaybeSpotlightTarget = componentWithCondition(
	() => fg('jira_list_nin_merge') || fg('jira_list_conditional_formatting_onboarding'),
	SpotlightTarget,
	({ children }: PropsWithChildren<{}>) => children,
);

const AdditionalItemsWrapper = componentWithCondition(
	() => expVal('jsc_m2_hierarchy', 'isHierarchyEnabled', false),
	({ children }: PropsWithChildren<{}>) => <Box xcss={menuItemStyles}>{children}</Box>,
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
);
const PageActions = ({
	jql,
	filterId,
	isModifiedFilter,
	searchResultPageData,
	isQuickFiltersDisabled = false,
	HierarchyToggle,
	GroupByDropdown,
	HideDoneToggle,
	FiltersMenu,
	isIssueHierarchySupportEnabled,
	isHideDoneSupportEnabled,
	isHideDoneWorkItemsEnabled,
	isGridNavigationEnabled,
	SaveResetViewSettingsButton,
	isInlineFieldConfigEnabled,
	onChangeFilter,
	onChangeJqlAndGroupBy,
}: Props): ReactElement<ComponentProps<typeof ActionsContainer>> => {
	markOnce(marks.PAGE_ACTIONS_START);
	useLayoutEffect(() => {
		markOnce(marks.PAGE_ACTIONS_END);
	}, []);

	const savedFilter = isFilterId(filterId) ? filterId : undefined; // filter out non-standard PIN named filters
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const fireAdvanceSearchClickAnalytics = (
		e: React.MouseEvent<Element> | React.KeyboardEvent<Element>,
	) => {
		const leftClick = e.type === 'contextmenu';
		const analyticsEvent = createAnalyticsEvent({ action: 'clicked', actionSubject: 'button' });
		const withCmd = e.ctrlKey || e.metaKey;
		fireUIAnalytics(analyticsEvent, 'advancedSearchButton', {
			openedIn: leftClick && !withCmd ? 'sameTab' : 'newTab',
		});
	};

	const jqlString = isModifiedFilter ? jql : undefined;
	const advancedSearchHref = fg('nin_fix_stringify_in_go_to_advanced')
		? `/issues/?${stringifyOld({ jql: jqlString, filter: filterId })}`
		: `/issues/?${stringify({ jql: jqlString, filter: filterId })}`;

	const HierarchyToggleComponent =
		HierarchyToggle !== null &&
		HierarchyToggle !== undefined &&
		expVal('jsc_m2_hierarchy', 'isHierarchyEnabled', false) ? (
			<DropdownItem key={HIERARCHY_TOGGLE} component={HierarchyToggleWrapper}>
				{HierarchyToggle}
			</DropdownItem>
		) : null;

	const HideDoneToggleComponent =
		HideDoneToggle !== null && HideDoneToggle !== undefined && fg('jsc_list_hide_done_items') ? (
			<SpotlightTarget name="nin-hide-done-toggle-spotlight">
				<DropdownItem key={HIDE_DONE_TOGGLE} component={HideDoneToggleWrapper}>
					{HideDoneToggle}
				</DropdownItem>
			</SpotlightTarget>
		) : null;

	const viewOptions =
		HideDoneToggleComponent || HierarchyToggleComponent ? (
			<>
				{HideDoneToggleComponent}
				{HierarchyToggleComponent}
			</>
		) : null;

	const additionalMenuItems = (
		<DropdownItem
			key={GO_TO_ALL_ISSUES}
			onClick={(e) => fireAdvanceSearchClickAnalytics(e)}
			href={advancedSearchHref}
		>
			<AdditionalItemsWrapper>
				{formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.allIssuesIssueTermRefresh
						: messages.allIssues,
				)}
			</AdditionalItemsWrapper>
		</DropdownItem>
	);

	const customExportTrigger = (
		isOpen: boolean,
		triggerButtonProps: CustomTriggerProps<HTMLElement>,
	) => {
		const { triggerRef, ...props } = triggerButtonProps;
		return (
			<FilterIconButton
				customTriggerProps={{
					...props,
					ref: triggerRef,
				}}
				icon={ExportIcon}
				label={formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.exportIssuesButtonIconLabelIssueTermRefresh
						: messages.exportIssuesButtonIconLabel,
				)}
				isSelected={isOpen}
			/>
		);
	};

	const customExportDisabled = (
		<FilterIconButton
			isDisabled
			icon={ExportIcon}
			label={formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.exportIssuesButtonIconLabelIssueTermRefresh
					: messages.exportIssuesButtonIconLabel,
			)}
		/>
	);

	const [isNaturalLanguageMode] = useNaturalLanguageMode();
	const enableQuickFilters = !isNaturalLanguageMode && !isQuickFiltersDisabled;

	const viewSwitcher = (
		<StyledPageAction>
			<MaybeSpotlightTarget name="project-issue-navigator.ui.page-actions.view-switcher">
				<ViewSwitcher filterId={filterId} />
			</MaybeSpotlightTarget>
		</StyledPageAction>
	);

	const exportMenu = (
		<StyledPageAction>
			<AsyncExportIssues
				jql={jql}
				filterId={savedFilter}
				isModifiedFilter={isModifiedFilter}
				showEcosystemApps={false}
				isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
				customTriggerButton={customExportTrigger}
				customDisabledButton={customExportDisabled}
			/>
		</StyledPageAction>
	);

	return (
		<ActionsContainer>
			{enableQuickFilters &&
				!expVal('jira-issue-navigator-saved-filters-menu', 'isEnabled', false) && (
					<StyledPageAction>
						<QuickFiltersDropdown onChangeFilter={onChangeFilter} />
					</StyledPageAction>
				)}
			{FiltersMenu && expVal('jira-issue-navigator-saved-filters-menu', 'isEnabled', false) && (
				<StyledPageAction>{FiltersMenu}</StyledPageAction>
			)}
			{GroupByDropdown && <StyledPageAction>{GroupByDropdown}</StyledPageAction>}
			{!(isVisualRefreshEnabled() && fg('visual-refresh-headers-buttons')) && (
				<IssueNavigatorFeatureGuard featureKey="isExportMenuEnabled" on={exportMenu} />
			)}
			<IssueNavigatorFeatureGuard featureKey="isViewSwitcherEnabled" on={viewSwitcher} />
			{SaveResetViewSettingsButton && expVal('saved_list_view', 'isEnabled', false) && (
				<div css={styles.saveResetButtonWrapper}>{SaveResetViewSettingsButton}</div>
			)}
			<StyledPageAction>
				<MaybeSpotlightTarget name="project-issue-navigator.ui.page-actions.meatball-menu">
					<MeatballMenu
						jql={jql}
						filterId={savedFilter}
						searchResultPageData={searchResultPageData}
						additionalMenuItems={additionalMenuItems}
						viewOptions={viewOptions}
						isIssueHierarchySupportEnabled={isIssueHierarchySupportEnabled}
						isHideDoneSupportEnabled={isHideDoneSupportEnabled}
						isHideDoneWorkItemsEnabled={isHideDoneWorkItemsEnabled}
						isModifiedFilter={isModifiedFilter}
						isGridNavigationEnabled={isGridNavigationEnabled}
						isInlineFieldConfigEnabled={isInlineFieldConfigEnabled}
						onChangeJqlAndGroupBy={onChangeJqlAndGroupBy}
					/>
				</MaybeSpotlightTarget>
			</StyledPageAction>
		</ActionsContainer>
	);
};

export default PageActions;

const styles = cssMap({
	saveResetButtonWrapper: {
		marginTop: 0,
		marginRight: token('space.050'),
		marginBottom: 0,
		marginLeft: token('space.negative.050'),
		minWidth: '0px',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledPageAction = styled.div({
	marginTop: 0,
	marginRight: token('space.050'),
	marginBottom: 0,
	marginLeft: token('space.050'),
	minWidth: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		marginRight: 0,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ActionsContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
	flexWrap: 'nowrap',
});

const menuItemStyles = xcss({
	paddingTop: 'space.075',
	paddingBottom: 'space.075',
	paddingLeft: 'space.050',
	paddingRight: 'space.050',
});
